/* V208 — Per-agent stats dashboard page.
 * URL: /dashboard/agent/<slug>
 * Shows: totals, daily chart, top origins, recent executions stream.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AGENT_LABELS = {
  "helpdesk": "Helpdesk Agent",
  "review-reply": "Review Reply Agent",
  "cart-recovery": "Cart Recovery Agent",
  "product-desc": "Product Description Optimizer",
  "newsletter": "Newsletter Agent",
  "gdpr-watch": "GDPR + A11y Watch",
  "social-media": "Social Media Agent",
  "review-moderator": "Review Moderator Agent",
  "product-importer": "Product Importer Agent",
  "pricing-intel": "Pricing Intel Agent",
  "customer-onboarding": "Customer Onboarding Agent",
  "behavior-analytics": "Behavior Analytics Agent",
  "sales-hunter": "Sales Hunter Agent",
  "competitive-manager": "Competitive Manager Agent",
  "seo-pages-generator": "SEO Pages Generator",
};

export default function AgentStatsPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true); setErr("");
    axios.get(`${API}/me/agent-stats/${slug}?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        if (e.response?.status === 401) setErr("Musíš byť prihlásený.");
        else setErr(e.response?.data?.detail || "load_failed");
      })
      .finally(() => setLoading(false));
  }, [slug, days]);

  const agentName = AGENT_LABELS[slug] || slug;
  const totals = data?.totals;
  const maxDailyRuns = Math.max(1, ...((data?.daily || []).map((d) => d.runs)));

  return (
    <div className="min-h-screen bg-zinc-50 p-6" data-testid="agent-stats-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <a href="/dashboard?tab=api-keys" className="text-xs text-purple-600 hover:underline">← Späť na API Keys</a>
            <h1 className="text-3xl font-black text-gray-900 mt-1">🤖 {agentName}</h1>
            <p className="text-sm text-gray-500 mt-1">Štatistiky behov, klientov a chýb</p>
          </div>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                  data-testid="stats-days-select"
                  className="bg-white border border-gray-300 rounded px-3 py-2 text-sm">
            <option value={7}>Posledných 7 dní</option>
            <option value={30}>Posledných 30 dní</option>
            <option value={90}>Posledných 90 dní</option>
          </select>
        </div>

        {err && <div className="bg-rose-50 border border-rose-300 text-rose-700 p-3 text-sm" data-testid="stats-error">{err}</div>}
        {loading && <div className="text-gray-500 text-sm">Načítavam…</div>}

        {!loading && totals && (
          <>
            {/* Totals */}
            <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="stats-totals">
              <Stat label="Behov" value={totals.runs} accent="text-blue-600" />
              <Stat label="Úspešnosť" value={`${totals.success_rate}%`} accent="text-emerald-600" />
              <Stat label="Chýb" value={totals.errors} accent="text-rose-600" />
              <Stat label="API keys" value={totals.api_keys_count} accent="text-gray-900" />
            </section>

            {/* Daily chart */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm mb-6">
              <h3 className="font-black mb-3">📈 Denný timeline</h3>
              {(data.daily || []).length === 0 ? (
                <div className="text-gray-400 text-sm py-8 text-center">
                  Zatiaľ žiadne behy. Po prvom použití agenta sa tu zobrazí graf.
                </div>
              ) : (
                <div className="space-y-1" data-testid="stats-daily">
                  {(data.daily || []).map((d) => (
                    <div key={d.date} className="flex items-center gap-3 text-xs">
                      <div className="w-24 text-gray-500 font-mono">{d.date}</div>
                      <div className="flex-1 bg-gray-100 h-6 rounded relative overflow-hidden">
                        <div className="bg-purple-500 h-full"
                             style={{ width: `${(d.runs / maxDailyRuns) * 100}%` }} />
                        <span className="absolute inset-0 flex items-center px-2 text-[10px] font-bold text-gray-700">
                          {d.runs} behov · {d.errors} chýb · {d.avg_latency_ms}ms avg
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Top origins */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm mb-6">
              <h3 className="font-black mb-3">🌐 Top stránky (origins)</h3>
              {(data.top_origins || []).length === 0 ? (
                <div className="text-gray-400 text-sm py-4 text-center">Žiadne origins.</div>
              ) : (
                <table className="w-full text-xs" data-testid="stats-origins">
                  <thead className="text-gray-500 uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="text-left p-2">Doména</th>
                      <th className="text-right p-2">Behov</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.top_origins || []).map((o) => (
                      <tr key={o.origin} className="border-t border-gray-100">
                        <td className="p-2 font-mono text-gray-700">{o.origin || "(no origin)"}</td>
                        <td className="p-2 text-right font-bold">{o.runs}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            {/* Recent executions */}
            <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
              <h3 className="font-black mb-3">🔍 Posledných 20 behov</h3>
              {(data.recent || []).length === 0 ? (
                <div className="text-gray-400 text-sm py-4 text-center">Žiadne behy.</div>
              ) : (
                <div className="space-y-2" data-testid="stats-recent">
                  {(data.recent || []).map((r) => (
                    <div key={r.execution_id} className="border border-gray-200 rounded p-3 text-xs">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded ${r.ok ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                          {r.ok ? "✓ OK" : "✗ ERR"}
                        </span>
                        <span className="text-gray-400">{r.started_at} · {r.latency_ms}ms · {r.locale}</span>
                      </div>
                      <div className="text-gray-600"><strong>Q:</strong> {r.message_preview || "—"}</div>
                      <div className="text-gray-900 mt-1"><strong>A:</strong> {r.reply_preview || "—"}</div>
                      <div className="text-gray-400 mt-1 font-mono text-[10px]">{r.origin}</div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className={`text-3xl font-black mt-1 ${accent || "text-gray-900"}`}>{value}</div>
    </div>
  );
}
